import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "./FlechasCarruselPremios";
import {
  DotButton,
  useDotButton,
} from "./PuntitosCarruselPremios";
import ImageWithLoader from "../ImageWithLoader";

// 🔥 Nuevo lightbox moderno
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";

const API_BASE = "https://api.fallaplazarodrigo.es:999";

const TWEEN_FACTOR_BASE = 0.52;

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max);

const PremiosCarruselComponent = ({ yearData, options }) => {
  const photos = yearData?.photos || [];

  // 👉 Normalizamos rutas e info para carrusel + lightbox
  const slides = photos.map((photo, index) => {
    let src = photo.src || "";
    let thumb = photo.srcMin || photo.src || "";
    const title = photo.title || `Foto ${index + 1}`;

    if (src && !src.startsWith("http")) {
      src = `${API_BASE}${src}`;
    }
    if (thumb && !thumb.startsWith("http")) {
      thumb = `${API_BASE}${thumb}`;
    }

    return {
      src,
      thumb,
      description: title,
    };
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const tweenNodes = useRef([]);

  // Estado para el lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const setTweenNodes = useCallback((emblaApi) => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) =>
      slideNode.querySelector(".embla__slide__number")
    );
  }, []);

  const tweenScale = useCallback((emblaApi, eventName) => {
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === "scroll";

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress);
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const tweenValue =
          1 - Math.abs(diffToTarget * TWEEN_FACTOR_BASE);
        const scale = numberWithinRange(
          tweenValue,
          0,
          1
        ).toString();
        const tweenNode = tweenNodes.current[slideIndex];
        if (tweenNode) {
          tweenNode.style.transform = `scale(${scale})`;
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    tweenScale(emblaApi);

    emblaApi.on("reInit", setTweenNodes).on("scroll", tweenScale);
  }, [emblaApi, setTweenNodes, tweenScale]);

  if (!slides.length) return null;

  return (
    <>
      <div className="embla">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((slide, index) => (
              <div className="embla__slide" key={index}>
                <button
                  type="button"
                  className="embla__slide__number"
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                >
                  <ImageWithLoader
                    src={slide.thumb}
                    alt={slide.description}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="embla__controls">
          <div className="embla__buttons">
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>

          <div className="embla__dots">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"embla__dot".concat(
                  index === selectedIndex
                    ? " embla__dot--selected"
                    : ""
                )}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox full-screen, pensado para móvil */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={slides.map((s) => ({
          src: s.src,
          description: s.description,
        }))}
        plugins={[Captions]}
        carousel={{ finite: false }} // permite loop + swipe
        render={{
          slide: ({ slide }) => (
            <figure style={{ margin: 0 }}>
              <img
                src={slide.src}
                alt={slide.description || ""}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "100vh",
                  objectFit: "contain",
                }}
              />
              {slide.description && (
                <figcaption
                  style={{
                    textAlign: "center",
                    padding: "0.5rem 1rem",
                    fontSize: "0.9rem",
                  }}
                >
                  {slide.description}
                </figcaption>
              )}
            </figure>
          ),
        }}
      />
    </>
  );
};

export default PremiosCarruselComponent;
